import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { scoreTone } from "@/lib/api";

export function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

export function ScoreRing({ score, size = 180, stroke = 12, showLabel = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const val = useCountUp(score, inView);
  const tone = scoreTone(score);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (val / 100) * circ;

  return (
    <div ref={ref} className="relative inline-flex items-center justify-center" data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E9EEF6" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={tone.color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading font-extrabold tracking-tight" style={{ fontSize: size * 0.26, color: tone.color }}>
          {val}
        </span>
        {showLabel && <span className="font-mono text-slate-400 -mt-1" style={{ fontSize: size * 0.07 }}>/100</span>}
      </div>
    </div>
  );
}

export function ScoreBar({ label, score }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const val = useCountUp(score, inView);
  const tone = scoreTone(score);
  return (
    <div ref={ref} className="border border-slate-200 rounded-xl bg-white p-4 hover-lift hover:border-brand/40" data-testid={`score-card-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-baseline justify-between mb-2.5">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <span className="font-heading font-bold text-2xl" style={{ color: tone.color }}>{val}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: tone.color, transition: "width 0.1s linear" }} />
      </div>
    </div>
  );
}
